<template>
  <div>
    <b-row class="mb-2">
      <b-col cols="12" class="d-flex align-items-center justify-content-between">
        <h3 class="mb-0">Planos</h3>
        <b-button variant="primary" @click="abrirModalCriar">
          <feather-icon icon="PlusIcon" class="mr-50" />Novo Plano
        </b-button>
      </b-col>
    </b-row>

    <b-row>
      <b-col v-for="plano in planos" :key="plano.id" cols="12" md="6" xl="4" class="mb-2">
        <b-card class="h-100 shadow-sm" border-variant="light">
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div>
              <h5 class="mb-0 font-weight-bold">{{ plano.nome }}</h5>
              <b-badge :variant="plano.ativo ? 'light-success' : 'light-secondary'" class="mt-50">
                {{ plano.ativo ? 'Ativo' : 'Inativo' }}
              </b-badge>
            </div>
            <div class="text-right">
              <div class="font-weight-bold text-primary" style="font-size:1.2rem;">
                R$ {{ Number(plano.preco_mensal).toFixed(2).replace('.', ',') }}<small class="text-muted font-weight-normal">/mês</small>
              </div>
              <div v-if="plano.preco_anual" class="text-muted small">
                R$ {{ Number(plano.preco_anual).toFixed(2).replace('.', ',') }}/ano
              </div>
            </div>
          </div>

          <p class="text-muted small mb-1" style="min-height:40px;">{{ plano.descricao || 'Sem descrição.' }}</p>

          <div class="mb-1">
            <div v-if="plano.recursos && plano.recursos.whitelabel" class="d-flex align-items-center text-success mb-25">
              <feather-icon icon="CheckIcon" class="mr-50" size="14" />
              <small>WhiteLabel incluso</small>
            </div>
            <div v-else class="d-flex align-items-center text-danger mb-25">
              <feather-icon icon="XIcon" class="mr-50" size="14" />
              <small>Sem WhiteLabel</small>
            </div>
            <div v-if="plano.recursos && plano.recursos.max_produtos" class="d-flex align-items-center text-muted mb-25">
              <feather-icon icon="PackageIcon" class="mr-50" size="14" />
              <small>Até {{ plano.recursos.max_produtos }} produtos</small>
            </div>
          </div>

          <div class="d-flex mt-auto">
            <b-button variant="outline-primary" size="sm" class="mr-1" @click="abrirModalEditar(plano)">
              <feather-icon icon="EditIcon" class="mr-25" />Editar
            </b-button>
            <b-button variant="outline-danger" size="sm" @click="confirmarExcluir(plano)">
              <feather-icon icon="TrashIcon" class="mr-25" />Excluir
            </b-button>
          </div>
        </b-card>
      </b-col>

      <b-col v-if="!planos.length && !loading" cols="12" class="text-center py-5 text-muted">
        <feather-icon icon="PackageIcon" size="48" class="mb-1" />
        <p>Nenhum plano cadastrado ainda.</p>
      </b-col>
    </b-row>

    <b-modal
      v-model="modalAberto"
      :title="editando ? 'Editar Plano' : 'Novo Plano'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      :ok-disabled="salvando"
      @ok.prevent="salvarPlano"
    >
      <b-form @submit.prevent="salvarPlano">
        <b-form-group label="Nome do Plano" label-for="nome">
          <b-form-input id="nome" v-model="form.nome" placeholder="Ex: WhiteLabel Pro" required />
        </b-form-group>

        <b-form-group label="Descrição" label-for="descricao">
          <b-form-textarea id="descricao" v-model="form.descricao" rows="2" placeholder="Descrição do plano" />
        </b-form-group>

        <b-row>
          <b-col cols="6">
            <b-form-group label="Preço Mensal (R$)" label-for="preco_mensal">
              <b-form-input id="preco_mensal" v-model="form.preco_mensal" type="number" min="0" step="0.01" required />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group label="Preço Anual (R$)" label-for="preco_anual">
              <b-form-input id="preco_anual" v-model="form.preco_anual" type="number" min="0" step="0.01" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Recursos">
          <b-form-checkbox v-model="form.recursos.whitelabel" class="mb-50">
            Incluir WhiteLabel (loja personalizada)
          </b-form-checkbox>
          <b-row class="mt-50">
            <b-col cols="6">
              <b-form-group label="Máx. Produtos" label-for="max_produtos" class="mb-0">
                <b-form-input id="max_produtos" v-model="form.recursos.max_produtos" type="number" min="0" placeholder="Ilimitado" />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form-group>

        <b-form-group label="Status">
          <b-form-checkbox v-model="form.ativo" :value="1" :unchecked-value="0">
            Plano ativo
          </b-form-checkbox>
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow, BCol, BCard, BButton, BBadge, BModal, BForm, BFormGroup,
  BFormInput, BFormTextarea, BFormCheckbox,
} from 'bootstrap-vue'
import { ref, onMounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BRow, BCol, BCard, BButton, BBadge, BModal, BForm, BFormGroup,
    BFormInput, BFormTextarea, BFormCheckbox,
  },
  setup() {
    const toast = useToast()
    const planos = ref([])
    const loading = ref(false)
    const modalAberto = ref(false)
    const editando = ref(null)
    const salvando = ref(false)

    const formVazio = () => ({
      nome: '',
      descricao: '',
      preco_mensal: 0,
      preco_anual: null,
      recursos: { whitelabel: false, max_produtos: null },
      ativo: 1,
    })
    const form = ref(formVazio())

    const carregar = async () => {
      loading.value = true
      try {
        planos.value = await store.dispatch('appPlanos/fetchPlanos')
      } finally {
        loading.value = false
      }
    }

    const abrirModalCriar = () => {
      editando.value = null
      form.value = formVazio()
      modalAberto.value = true
    }

    const abrirModalEditar = plano => {
      editando.value = plano
      form.value = {
        nome: plano.nome,
        descricao: plano.descricao || '',
        preco_mensal: plano.preco_mensal,
        preco_anual: plano.preco_anual || null,
        recursos: { whitelabel: false, max_produtos: null, ...(plano.recursos || {}) },
        ativo: plano.ativo,
      }
      modalAberto.value = true
    }

    const salvarPlano = async () => {
      salvando.value = true
      try {
        const payload = { ...form.value }
        if (editando.value) {
          await store.dispatch('appPlanos/atualizarPlano', { id: editando.value.id, ...payload })
          await carregar()
          toast({ component: ToastificationContent, props: { title: 'Plano atualizado!', icon: 'CheckIcon', variant: 'success' } })
        } else {
          await store.dispatch('appPlanos/criarPlano', payload)
          await carregar()
          toast({ component: ToastificationContent, props: { title: 'Plano criado!', icon: 'CheckIcon', variant: 'success' } })
        }
        modalAberto.value = false
      } catch (e) {
        toast({ component: ToastificationContent, props: { title: 'Erro ao salvar', icon: 'AlertTriangleIcon', variant: 'danger', text: e?.response?.data?.error || e.message } })
      } finally {
        salvando.value = false
      }
    }

    const confirmarExcluir = async plano => {
      if (!window.confirm(`Desativar o plano "${plano.nome}"?`)) return
      try {
        await store.dispatch('appPlanos/excluirPlano', plano.id)
        planos.value = planos.value.filter(p => p.id !== plano.id)
        toast({ component: ToastificationContent, props: { title: 'Plano desativado', icon: 'CheckIcon', variant: 'success' } })
      } catch (e) {
        toast({ component: ToastificationContent, props: { title: 'Erro ao excluir', icon: 'AlertTriangleIcon', variant: 'danger' } })
      }
    }

    onMounted(carregar)

    return { planos, loading, modalAberto, editando, salvando, form, abrirModalCriar, abrirModalEditar, salvarPlano, confirmarExcluir }
  },
}
</script>
